import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, RouteConfigLoadEnd, RouteConfigLoadStart, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { initializeApp } from "firebase/app";
import { environment } from './../environments/environment';
import { Footer } from "./shared/footer/footer";
import { LoadingService } from './loading.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Footer],
  templateUrl: './app.html',
  host: {
    class: 'flex min-h-screen min-h-dvh flex-col'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App {
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);
  private readonly supportedLanguages = ['de', 'fr', 'it', 'en'];

  loadingService = inject(LoadingService);
  router = inject(Router);

  showFooter = signal<boolean>(true);

  constructor() {
    this.translate.addLangs(this.supportedLanguages);
    this.translate.setFallbackLang('de');

    const browserLanguage = this.translate.getBrowserLang()?.toLowerCase() ?? '';
    const language = this.supportedLanguages.includes(browserLanguage) ? browserLanguage : 'de';
    this.translate.use(language);
    this.updateHtmlLang(language);

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.updateHtmlLang(event.lang));

    initializeApp(environment.firebase);

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof RouteConfigLoadStart) {
          this.loadingService.showProgressBar.set(true);
        } else if (event instanceof RouteConfigLoadEnd) {
          this.loadingService.showProgressBar.set(false);
        } else if (event instanceof NavigationEnd) {
          const url = event.urlAfterRedirects;
          this.showFooter.set(!url.startsWith('/embed'));

          const langSegment = url.split('/')[1] ?? '';
          if (this.supportedLanguages.includes(langSegment)) {
            this.translate.use(langSegment);
          }
        }
      });
  }

  private updateHtmlLang(lang: string): void {
    this.document.documentElement.setAttribute('lang', lang);
  }

  protected readonly title = signal('webapp');
}
